import { useEffect, useMemo, useState, type CSSProperties } from 'react';

import { RUN_TOOL_CARDS } from '../../components/screens/RunToolsScreen';
import type { RunToolCard } from '../../components/screens/helpers/runScreenTypes';
import { IconAnthropic, IconOpenAI, IconZhipu } from './appIcons';
import { runRequestJson } from './appRunData';

export type ToolGroupId = 'models' | 'code' | 'contracts';

interface ToolGroup {
  id: ToolGroupId;
  title: string;
}

export const TOOL_GROUPS: ToolGroup[] = [
  { id: 'models', title: 'Models' },
  { id: 'code', title: 'Code and web security' },
  { id: 'contracts', title: 'Smart contracts' }
];

// What each tool targets; the run page's cards only distinguish models from tools.
const SMART_CONTRACT_TOOL_IDS = new Set(['pashov', 'sc-auditor', 'quillshield', 'cyfrin', 'forefy']);

// Models carry no tag, so give them the role they play in a scan.
export const MODEL_ROLES: Record<string, string> = {
  'gpt-5-6': 'Baseline pass',
  'glm-5-3': 'Baseline pass',
  'daybreak-blue': 'Deep reasoning',
  'claude-opus-5-5': 'Independent pass'
};

export const getGroupId = (tool: RunToolCard): ToolGroupId => {
  if (tool.kind === 'model') {
    return 'models';
  }

  return SMART_CONTRACT_TOOL_IDS.has(tool.id) ? 'contracts' : 'code';
};

// Each model shows its maker's mark.
export const MODEL_LOGOS: Record<string, typeof IconOpenAI> = {
  'gpt-5-6': IconOpenAI,
  'glm-5-3': IconZhipu,
  'daybreak-blue': IconOpenAI,
  'claude-opus-5-5': IconAnthropic
};

interface AgentDiscoveryResponse {
  skillGroups?: Record<string, string[]>;
  agents?: Array<{ name: string; models: string[] }>;
}

interface CatalogTool extends RunToolCard {
  group: ToolGroupId;
}

const modelCardId = (model: string): string => {
  const normalized = model.toLowerCase();

  if (normalized.includes('daybreak')) return 'daybreak-blue';
  if (normalized.includes('claude-opus-5-5')) return 'claude-opus-5-5';
  if (normalized.includes('glm-5.3')) return 'glm-5-3';
  if (normalized.includes('gpt-5.6')) return 'gpt-5-6';
  return model;
};

const fallbackTitle = (id: string): string => {
  const leaf = id.split('/').at(-1) ?? id;

  return leaf.split(/[-_]/).filter(Boolean).map((part) => {
    return part.length <= 3 ? part.toUpperCase() : `${part.charAt(0).toUpperCase()}${part.slice(1)}`;
  }).join(' ');
};

const buildCatalog = (discovery?: AgentDiscoveryResponse): CatalogTool[] => {
  if (!discovery) {
    return RUN_TOOL_CARDS.map((tool) => ({ ...tool, group: getGroupId(tool) }));
  }

  const staticById = new Map(RUN_TOOL_CARDS.map((tool) => [tool.id, tool]));
  const models = Array.from(new Set(
    (discovery.agents ?? []).flatMap((agent) => agent.models)
  ));
  const modelTools: CatalogTool[] = models.map((model) => {
    const cardId = modelCardId(model);
    const known = staticById.get(cardId);

    return known
      ? { ...known, group: 'models' }
      : {
        id: model,
        kind: 'model',
        coverage: 'deep',
        title: fallbackTitle(model),
        description: 'Model available for independent security analysis passes.',
        group: 'models'
      };
  });
  const smartContractSkills = new Set(discovery.skillGroups?.['smart-contract'] ?? []);
  const webApiSkills = new Set(discovery.skillGroups?.['web-api'] ?? []);
  const skills = Array.from(new Set([...smartContractSkills, ...webApiSkills]));
  const skillTools: CatalogTool[] = skills.map((skill) => {
    const known = staticById.get(skill);

    return {
      ...(known ?? {
        id: skill,
        kind: 'skill' as const,
        coverage: 'deep' as const,
        title: fallbackTitle(skill),
        description: 'Security analysis skill enabled by the backend.',
        tag: 'Security tool'
      }),
      group: smartContractSkills.has(skill) && !webApiSkills.has(skill) ? 'contracts' : 'code'
    };
  });

  return [...modelTools, ...skillTools];
};

/** Monograms get a stable, muted hue each, so tools without a logo still look distinct. */
const getMonogramHue = (id: string): number => {
  return [...id].reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 360, 7);
};

export function AppToolMark({ tool }: { tool: RunToolCard }) {
  const Logo = MODEL_LOGOS[tool.id];

  if (Logo) {
    return (
      <span className="app-tool-mark brand" aria-hidden={true}>
        <Logo aria-hidden={true} />
      </span>
    );
  }

  if (tool.logo) {
    return (
      <span className="app-tool-mark" aria-hidden={true}>
        <img src={tool.logo} alt="" loading="lazy" />
      </span>
    );
  }

  return (
    <span className="app-tool-mark monogram" style={{ '--tool-hue': getMonogramHue(tool.id) } as CSSProperties} aria-hidden={true}>
      {tool.title.charAt(0)}
    </span>
  );
}

function AppToolsView() {
  const [discovery, setDiscovery] = useState<AgentDiscoveryResponse>();
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    runRequestJson<AgentDiscoveryResponse>('/agents')
      .then((response) => {
        if (!cancelled) setDiscovery(response);
      })
      .catch((loadError: unknown) => {
        console.error('Failed to load tools:', loadError);
        if (!cancelled) setError('Live tool availability could not be loaded. Showing the known catalog.');
      });

    return () => { cancelled = true; };
  }, []);

  const catalog = useMemo(() => buildCatalog(discovery), [discovery]);

  return (
    <div className="app-scroll">
      <div className="app-tools">
        {error && <p className="app-widget-error">{error}</p>}
        {TOOL_GROUPS.map((group) => {
          const tools = catalog.filter((tool) => tool.group === group.id);

          if (tools.length === 0) {
            return null;
          }

          return (
            <section key={group.id} className="app-tool-group" aria-labelledby={`app-tools-${group.id}`}>
              <h2 id={`app-tools-${group.id}`}>{group.title}</h2>
              <ul>
                {tools.map((tool) => (
                  <li key={tool.id} className="app-tool">
                    <AppToolMark tool={tool} />
                    <div className="app-tool-copy">
                      <strong>{tool.title}</strong>
                      <span>{tool.description}</span>
                    </div>
                    <span className="app-tool-kind">{MODEL_ROLES[tool.id] ?? tool.tag ?? 'Security tool'}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default AppToolsView;
